import {
  getDownloadURL,
  ref
} from 'firebase/storage';
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { updatePassword } from 'firebase/auth';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import {
  Backpack,
  Bell,
  BookOpenCheck,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  ChevronDown,
  CircleDollarSign,
  Home,
  LogOut,
  Megaphone,
  Menu,
  MessageCircleMore,
  MessageCircleQuestion,
  Pencil,
  Phone,
  Reply,
  Send,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
  X
} from 'lucide-react';
import { useAuth } from '../../core/auth/AuthProvider';
import { auth, db, storage } from '../../core/firebase/firebase';
import { hasRegisteredNotificationToken, requestBrowserNotifications } from '../../core/notifications/browserNotifications';
import ParentFinanceSection from './ParentFinanceSection';
import ParentProfileForm from './ParentProfileForm';
import ParentSchoolSupplies from '../supplies/ParentSchoolSupplies';
import { studentClassId } from '../../core/school/classes';

const parentName = (value) =>
  value?.fullName || value?.fullname || value?.parentFullName || value?.name || '';

const childName = (value) =>
  value?.fullName || value?.fullname || value?.studentFullName || value?.name || 'Élève';

const normalize = (value) => String(value || '').trim().toLowerCase();
const PARENT_ONBOARDING_VERSION = 1;

const COMPLAINT_STATUS_LABELS = {
  sent: 'Envoyée',
  in_review: 'En cours',
  waiting_parent: 'En attente de votre réponse',
  resolved: 'Traitée',
  closed: 'Fermée'
};

const COMPLAINT_CATEGORY_LABELS = {
  administration: 'Administration',
  pedagogie: 'Pédagogie',
  paiement: 'Paiement',
  autre: 'Autre'
};

function complaintStatus(value) {
  const status = normalize(value);
  if (['sent', 'envoyee', 'envoyée', 'nouvelle', 'new'].includes(status)) return 'sent';
  if (['in_review', 'en_cours', 'encours', 'processing', 'en traitement'].includes(status)) return 'in_review';
  if (['waiting_parent', 'attente_parent', 'en attente parent'].includes(status)) return 'waiting_parent';
  if (['resolved', 'done', 'traitee', 'traitée'].includes(status)) return 'resolved';
  if (['closed', 'fermee', 'fermée', 'archivee', 'archivée'].includes(status)) return 'closed';
  return status || 'sent';
}

function complaintStatusLabel(value) {
  return COMPLAINT_STATUS_LABELS[complaintStatus(value)] || value || 'Envoyée';
}

function complaintCategoryLabel(value) {
  return COMPLAINT_CATEGORY_LABELS[normalize(value)] || value || 'Autre';
}

const MENU = [
  { id: 'dashboard', label: 'Tableau de bord', icon: Home },
  { id: 'children', label: 'Mes enfants', icon: UsersRound },
  { id: 'attendance', label: 'Absences et retards', icon: ClipboardCheck },
  { id: 'homework', label: 'Travail à la maison', icon: BookOpenCheck },
  { id: 'supplies', label: 'Fournitures scolaires', icon: Backpack },
  { id: 'announcements', label: 'Annonces', icon: Megaphone },
  { id: 'payments', label: 'Contrat et paiements', icon: CircleDollarSign },
  { id: 'timetable', label: 'Emploi du temps', icon: CalendarDays },
  { id: 'complaints', label: 'Réclamations', icon: MessageCircleQuestion },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'profile', label: 'Mon profil', icon: UserRound }
];

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value) {
  const ms = toMillis(value);
  if (!ms) return 'Date à confirmer';
  return new Intl.DateTimeFormat('fr-TN', { day: '2-digit', month: 'short', year: 'numeric' }).format(ms);
}

function formatDateTime(value) {
  const ms = toMillis(value);
  if (!ms) return 'Date à confirmer';
  return new Intl.DateTimeFormat('fr-TN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }).format(ms);
}

function matchesSelectedChild(item, child) {
  if (!child) return false;
  const childId = child.id;
  const classId = child.classId || child.class?.id || child.className;
  const level = child.level || child.requestedLevel;
  const audience = normalize(item.audienceType || item.targetType || item.scope);

  if (['school', 'ecole', 'all', 'general'].includes(audience)) return true;
  if (item.studentId && item.studentId === childId) return true;
  if (Array.isArray(item.studentIds) && item.studentIds.includes(childId)) return true;
  if (classId && (item.classId === classId || item.className === classId)) return true;
  if (Array.isArray(item.classIds) && classId && item.classIds.includes(classId)) return true;
  if (level && (item.level === level || item.levelId === level)) return true;
  if (!item.studentId && !item.classId && !item.level && !item.audienceType && !item.targetType) return true;
  return false;
}

function SectionTitle({ eyebrow, title, description }) {
  return <div className="parent-section-title">
    <p className="eyebrow">{eyebrow}</p>
    <h2>{title}</h2>
    {description && <p>{description}</p>}
  </div>;
}

export default function ParentPortal() {
  const { user, profile, isParent, logout } = useAuth();
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [homework, setHomework] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [timetables, setTimetables] = useState([]);

  const [classTimetableUrl, setClassTimetableUrl] = useState('');
const [classTimetableLoading, setClassTimetableLoading] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [complaints, setComplaints] = useState([]);
  const [activePage, setActivePage] = useState('dashboard');
  const [selectedChildId, setSelectedChildId] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [notificationState, setNotificationState] = useState(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  );
  const [notificationRegistered, setNotificationRegistered] = useState(false);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationCheckDone, setNotificationCheckDone] = useState(false);
  const [notificationPromptOpen, setNotificationPromptOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [complaintForm, setComplaintForm] = useState({ category: 'administration', subject: '', message: '' });
  const [sendingComplaint, setSendingComplaint] = useState(false);
  const [selectedComplaintId, setSelectedComplaintId] = useState('');
  const [complaintMessages, setComplaintMessages] = useState([]);
  const [complaintReply, setComplaintReply] = useState('');
  const [sendingComplaintReply, setSendingComplaintReply] = useState(false);
  const [complaintFilter, setComplaintFilter] = useState('all');
  const [complaintComposerOpen, setComplaintComposerOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function safeCollection(name, constraints = []) {
      try {
        const source = constraints.length
          ? query(collection(db, name), ...constraints)
          : collection(db, name);
        const snapshot = await getDocs(source);
        return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      } catch (err) {
        console.warn(`ParentPortal — ${name}:`, err);
        return [];
      }
    }

    async function load() {
      setLoading(true);
      setError('');

      if (!profile?.parentId) {
        setError('Votre compte n’est relié à aucun dossier parent. Contactez l’administration.');
        setLoading(false);
        return;
      }

      try {
        const parentSnap = await getDoc(doc(db, 'parents', profile.parentId));
        if (!parentSnap.exists()) throw new Error('Dossier parent introuvable.');
        const parentData = { id: parentSnap.id, ...parentSnap.data() };
        if (!cancelled) setParent(parentData);

        const studentIds = Array.from(new Set([
          ...(Array.isArray(parentData.childrenIds) ? parentData.childrenIds : []),
          ...(Array.isArray(parentData.studentIds) ? parentData.studentIds : [])
        ].filter(Boolean)));

        let studentRows = [];
        if (studentIds.length) {
          const studentSnaps = await Promise.all(studentIds.map((id) => getDoc(doc(db, 'students', id))));
          studentRows = studentSnaps.filter((item) => item.exists()).map((item) => ({ id: item.id, ...item.data() }));
        } else {
          studentRows = await safeCollection('students', [where('parentIds', 'array-contains', profile.parentId)]);
        }

        if (cancelled) return;
        setChildren(studentRows);

        const remembered = localStorage.getItem(`le-defi:selected-child:${user.uid}`);
        const firstId = studentRows[0]?.id || '';
        const validRemembered = studentRows.some((item) => item.id === remembered) ? remembered : firstId;
        setSelectedChildId(validRemembered);

        const [announcementRows, homeworkRows, contractRows, paymentRows, timetableRows] = await Promise.all([
          safeCollection('announcements'),
          safeCollection('homework', [where('status', '==', 'approved')]),
          safeCollection('contracts', [where('parentId', '==', profile.parentId)]),
          safeCollection('payments', [where('parentId', '==', profile.parentId)]),
          safeCollection('timetables')
        ]);

        if (!cancelled) {
          setAnnouncements(announcementRows.filter((item) => item.published !== false).sort((a, b) => toMillis(b.publishedAt || b.createdAt) - toMillis(a.publishedAt || a.createdAt)));
          setHomework(homeworkRows.sort((a, b) => toMillis(a.deadline || a.dueDate) - toMillis(b.deadline || b.dueDate)));
          setContracts(contractRows);
          setPayments(paymentRows.sort((a, b) => toMillis(b.paidAt || b.createdAt) - toMillis(a.paidAt || a.createdAt)));
          setTimetables(timetableRows);
        }
      } catch (err) {
        console.error('ParentPortal — load:', err);
        if (!cancelled) setError(err.message || 'Impossible de charger votre espace parent.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [profile?.parentId, user?.uid]);

  useEffect(() => {
    if (!profile?.parentId) return undefined;
    const source = query(collection(db, 'reclamations'), where('parentId', '==', profile.parentId));
    return onSnapshot(source, (snapshot) => {
      const rows = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt));
      setComplaints(rows);
    }, (err) => console.warn('ParentPortal — reclamations live:', err));
  }, [profile?.parentId]);

  useEffect(() => {
    if (!profile?.parentId) {
      setNotifications([]);
      return undefined;
    }

    const source = query(
      collection(db, 'notifications'),
      where('parentId', '==', profile.parentId)
    );

    return onSnapshot(
      source,
      (snapshot) => {
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort(
            (a, b) =>
              toMillis(b.createdAt || b.updatedAt) -
              toMillis(a.createdAt || a.updatedAt)
          );

        setNotifications(rows);
      },
      (err) => console.warn('ParentPortal — notifications live:', err)
    );
  }, [profile?.parentId]);

  const selectedChild = useMemo(
    () => children.find((item) => item.id === selectedChildId) || children[0] || null,
    [children, selectedChildId]
  );

  useEffect(() => {
  let cancelled = false;

  async function loadClassTimetable() {
    const classId = studentClassId(selectedChild);

    if (!classId) {
      setClassTimetableUrl('');
      setClassTimetableLoading(false);
      return;
    }

    setClassTimetableLoading(true);

    try {
      const fileRef = ref(
        storage,
        `classTimetables/${classId}/emploi.png`
      );

      const url = await getDownloadURL(fileRef);

      if (!cancelled) {
        setClassTimetableUrl(url);
      }
    } catch (err) {
      if (err?.code === 'storage/object-not-found') {
        if (!cancelled) {
          setClassTimetableUrl('');
        }
      } else {
        console.error(
          'Chargement emploi du temps parent :',
          err
        );

        if (!cancelled) {
          setClassTimetableUrl('');
        }
      }
    } finally {
      if (!cancelled) {
        setClassTimetableLoading(false);
      }
    }
  }

  loadClassTimetable();

  return () => {
    cancelled = true;
  };
}, [selectedChild]);

  useEffect(() => {
    let cancelled = false;

    async function loadAttendance() {
      if (!selectedChildId) {
        setAttendanceRecords([]);
        setAttendanceError('');
        return;
      }

      setLoadingAttendance(true);
      setAttendanceError('');
      setAttendanceRecords([]);

      try {
        const attendanceQuery = query(
          collection(db, 'attendance'),
          where('studentId', '==', selectedChildId)
        );
        const snapshot = await getDocs(attendanceQuery);
        if (cancelled) return;

        const rows = snapshot.docs
          .map((attendanceDoc) => ({ id: attendanceDoc.id, ...attendanceDoc.data() }))
          .filter((item) => ['absent', 'late'].includes(normalize(item.status)))
          .sort((a, b) => {
            const dateComparison = String(b.date || '').localeCompare(String(a.date || ''));
            if (dateComparison !== 0) return dateComparison;
            return toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt);
          });

        setAttendanceRecords(rows);
      } catch (err) {
        console.error('ParentPortal — attendance:', err);
        if (!cancelled) setAttendanceError('Impossible de charger les absences et retards.');
      } finally {
        if (!cancelled) setLoadingAttendance(false);
      }
    }

    loadAttendance();
    return () => { cancelled = true; };
  }, [selectedChildId]);

  const filteredAnnouncements = useMemo(
    () => announcements.filter((item) => matchesSelectedChild(item, selectedChild)),
    [announcements, selectedChild]
  );

  const filteredHomework = useMemo(
    () => homework.filter((item) => matchesSelectedChild(item, selectedChild)),
    [homework, selectedChild]
  );

  const selectedTimetable = useMemo(
    () => timetables.filter((item) => matchesSelectedChild(item, selectedChild)),
    [timetables, selectedChild]
  );

  const selectedComplaints = useMemo(
    () => complaints.filter((item) => !selectedChild || !item.studentId || item.studentId === selectedChild.id),
    [complaints, selectedChild]
  );

  const unreadNotifications = useMemo(
    () => notifications.filter((item) => item.read !== true),
    [notifications]
  );

  const unreadCount = unreadNotifications.length;

  const visibleComplaints = useMemo(() => selectedComplaints.filter((item) => {
    const status = complaintStatus(item.status);
    if (complaintFilter === 'open') return !['resolved', 'closed'].includes(status);
    if (complaintFilter === 'resolved') return status === 'resolved';
    return complaintFilter === 'all' || status === complaintFilter;
  }), [selectedComplaints, complaintFilter]);

  const selectedComplaint = useMemo(
    () => selectedComplaints.find((item) => item.id === selectedComplaintId) || null,
    [selectedComplaints, selectedComplaintId]
  );

  useEffect(() => {
    if (activePage !== 'complaints') return;
    setSelectedComplaintId('');
    setComplaintComposerOpen(false);
  }, [activePage]);

  useEffect(() => {
    setComplaintMessages([]);
    setComplaintReply('');
    if (!selectedComplaintId) return undefined;
    return onSnapshot(collection(db, 'reclamations', selectedComplaintId, 'messages'), (snapshot) => {
      const rows = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
      setComplaintMessages(rows);
    }, (err) => console.warn('ParentPortal — complaint messages:', err));
  }, [selectedComplaintId]);

  const mustChangePassword = profile?.mustChangePassword === true;
  // The users flag alone is not enough: legacy/test accounts may already have it set.
  // Require an actually submitted and confirmed parent onboarding profile.
  const onboardingCompleted =
    Number(profile?.parentOnboardingVersion || 0) >= PARENT_ONBOARDING_VERSION &&
    Number(parent?.onboardingProfile?.schemaVersion || 0) >= PARENT_ONBOARDING_VERSION &&
    parent?.onboardingProfile?.confirmationAccepted === true;
  const acceptedContractsCount = contracts.filter(
  (item) =>
    normalize(item.status) === 'active' ||
    item.active === true ||
    normalize(item.status) === 'accepted' ||
    item.accepted === true
).length;
  const contractAccepted =
    children.length > 0 &&
    acceptedContractsCount === children.length;
  const schoolYear = parent?.schoolYear || '2026–2027';
  const displayName = parentName(parent) || profile?.displayName || 'Parent';
  const devVersionBadge = null;

  const notificationPromptStorageKey = user?.uid
    ? `le-defi:notification-prompt-snooze:${user.uid}`
    : '';

  const isIOSDevice =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/i.test(navigator.userAgent);

  const isStandaloneApp =
    typeof window !== 'undefined' &&
    (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator?.standalone === true
    );

  const needsIOSInstall = isIOSDevice && !isStandaloneApp;

  useEffect(() => {
    let active = true;

    setNotificationCheckDone(false);

    hasRegisteredNotificationToken({ user, db })
      .then((registered) => {
        if (!active) return;
        setNotificationRegistered(registered);
        setNotificationCheckDone(true);
      })
      .catch((err) => {
        console.warn('ParentPortal — notification token check:', err);
        if (active) setNotificationCheckDone(true);
      });

    return () => { active = false; };
  }, [user?.uid]);

  useEffect(() => {
    if (
      !notificationCheckDone ||
      notificationRegistered ||
      !onboardingCompleted ||
      mustChangePassword ||
      !user?.uid
    ) {
      if (notificationRegistered) {
        setNotificationPromptOpen(false);
      }
      return undefined;
    }

    if (
      notificationState === 'unsupported' &&
      !needsIOSInstall
    ) {
      return undefined;
    }

    if (typeof window === 'undefined') {
      return undefined;
    }

    const snoozedUntil = Number(
      window.localStorage.getItem(
        notificationPromptStorageKey
      ) || 0
    );

    if (snoozedUntil > Date.now()) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setNotificationPromptOpen(true);
    }, 700);

    return () => window.clearTimeout(timer);
  }, [
    notificationCheckDone,
    notificationRegistered,
    notificationState,
    onboardingCompleted,
    mustChangePassword,
    user?.uid,
    notificationPromptStorageKey,
    needsIOSInstall
  ]);

  function snoozeNotificationPrompt(days = 3) {
    if (
      typeof window !== 'undefined' &&
      notificationPromptStorageKey
    ) {
      const until =
        Date.now() +
        days * 24 * 60 * 60 * 1000;

      window.localStorage.setItem(
        notificationPromptStorageKey,
        String(until)
      );
    }

    setNotificationPromptOpen(false);
  }

  function selectChild(id) {
    setSelectedChildId(id);
    localStorage.setItem(`le-defi:selected-child:${user.uid}`, id);
  }

  if (!isParent) return <Navigate to="/login" replace />;
  if (loading) return <main className="parent-portal parent-loading"><div className="parent-loading-card">Chargement de votre espace…</div></main>;

  async function changePassword(event) {
    event.preventDefault();
    setError('');
    if (password.length < 8) return setError('Le mot de passe doit contenir au moins 8 caractères.');
    if (password !== confirm) return setError('Les mots de passe ne correspondent pas.');
    try {
      await updatePassword(auth.currentUser, password);
      await updateDoc(doc(db, 'users', user.uid), { mustChangePassword: false, passwordChangedAt: serverTimestamp() });
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError('Impossible de modifier le mot de passe. Reconnectez-vous puis réessayez.');
    }
  }

  async function enableNotifications() {
    if (notificationBusy) return null;

    setNotificationBusy(true);

    try {
      const result = await requestBrowserNotifications({
        user,
        profile,
        db
      });

      setNotificationState(result.permission);
      setNotificationRegistered(
        result.registered === true
      );
      setNotice(result.message);

      if (result.registered === true) {
        if (
          typeof window !== 'undefined' &&
          notificationPromptStorageKey
        ) {
          window.localStorage.removeItem(
            notificationPromptStorageKey
          );
        }

        setNotificationPromptOpen(false);
      } else if (result.permission === 'denied') {
        snoozeNotificationPrompt(7);
      }

      return result;
    } catch (err) {
      console.error(
        'ParentPortal — enable notifications:',
        err
      );

      setNotice(
        'Impossible d’activer les notifications sur cet appareil.'
      );

      return null;
    } finally {
      setNotificationBusy(false);
    }
  }

  async function markNotificationAsRead(notification) {
    if (!notification || notification.read === true) return;

    try {
      await updateDoc(
        doc(db, 'notifications', notification.id),
        {
          read: true,
          readAt: serverTimestamp()
        }
      );
    } catch (err) {
      console.error('ParentPortal — mark notification as read:', err);
      setError('Impossible de marquer cette notification comme lue.');
    }
  }

  async function markAllNotificationsAsRead() {
    if (!unreadNotifications.length) return;

    try {
      await Promise.all(
        unreadNotifications.map((notification) =>
          updateDoc(
            doc(db, 'notifications', notification.id),
            {
              read: true,
              readAt: serverTimestamp()
            }
          )
        )
      );
    } catch (err) {
      console.error('ParentPortal — mark all notifications as read:', err);
      setError('Impossible de marquer toutes les notifications comme lues.');
    }
  }



  async function submitComplaint(event) {
    event.preventDefault();
    if (!complaintForm.subject.trim() || !complaintForm.message.trim()) return;
    setSendingComplaint(true);
    setError('');
    try {
      const now = Timestamp.now();
      const payload = {
        parentId: profile.parentId,
        parentUid: user.uid,
        parentName: displayName,
        studentId: selectedChild?.id || null,
        studentName: selectedChild ? childName(selectedChild) : null,
        category: complaintForm.category,
        subject: complaintForm.subject.trim(),
        message: complaintForm.message.trim(),
        status: 'sent',
        statusHistory: [{
          status: 'sent',
          at: now,
          byUid: user.uid,
          byName: displayName,
          byType: 'parent'
        }],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: 'parent'
      };
      const created = await addDoc(collection(db, 'reclamations'), payload);
      setSelectedComplaintId('');
      setComplaintForm({ category: 'administration', subject: '', message: '' });
      setComplaintFilter('all');
      setComplaintComposerOpen(false);
      setNotice('Votre réclamation a bien été envoyée. Vous pouvez suivre son traitement ici.');
    } catch (err) {
      console.error(err);
      setError('Impossible d’envoyer la réclamation.');
    } finally {
      setSendingComplaint(false);
    }
  }

  async function submitComplaintReply(event) {
    event.preventDefault();
    const text = complaintReply.trim();
    if (!selectedComplaint || !text || sendingComplaintReply) return;
    const status = complaintStatus(selectedComplaint.status);
    if (['resolved', 'closed'].includes(status)) return;
    setSendingComplaintReply(true);
    setError('');
    try {
      await addDoc(collection(db, 'reclamations', selectedComplaint.id, 'messages'), {
        text,
        authorType: 'parent',
        authorUid: user.uid,
        authorName: displayName,
        parentId: profile.parentId,
        createdAt: serverTimestamp()
      });

      const updatePayload = {
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: 'parent'
      };
      if (status === 'waiting_parent') {
        updatePayload.status = 'in_review';
        updatePayload.statusHistory = arrayUnion({
          status: 'in_review',
          at: Timestamp.now(),
          byUid: user.uid,
          byName: displayName,
          byType: 'parent'
        });
      }
      await updateDoc(doc(db, 'reclamations', selectedComplaint.id), updatePayload);
      setComplaintReply('');
      setNotice('Votre réponse a été envoyée à l’administration.');
    } catch (err) {
      console.error(err);
      setError('Impossible d’envoyer votre réponse.');
    } finally {
      setSendingComplaintReply(false);
    }
  }

  if (mustChangePassword) {
    return <><main className="login-screen"><section className="login-card">
      <p className="eyebrow">PREMIÈRE CONNEXION</p><h1>Créez votre mot de passe</h1>
      <p className="muted">Votre code temporaire ne sera plus utilisable après cette étape.</p>
      <form className="form-stack" onSubmit={changePassword}>
        <label>Nouveau mot de passe<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
        <label>Confirmer<input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></label>
        {error && <div className="alert alert-error">{error}</div>}
        <button className="button button-primary">Enregistrer</button>
      </form>
    </section></main>{devVersionBadge}</>;
  }

  if (!onboardingCompleted) {
    return <><ParentProfileForm
      parent={parent}
      children={children}
      user={user}
      onboarding
      onSaved={() => window.location.reload()}
    />{devVersionBadge}</>;
  }

  const renderDashboard = () => {
    const upcomingHomework = filteredHomework.filter((item) => toMillis(item.deadline || item.dueDate) >= Date.now()).slice(0, 3);
    const familyRemaining = contracts.reduce(
      (sum, item) => sum + Number(item.remainingAmount || 0),
      0
    );
    return <>
      <section className="parent-hero-v0">
        <div>
          <p className="eyebrow">ESPACE PARENT</p>
          <h1>Bonjour {displayName} 👋</h1>
          <p>Voici l’essentiel pour {selectedChild ? childName(selectedChild) : 'votre famille'}.</p>
        </div>
        <div className={`parent-account-status ${contractAccepted ? 'is-complete' : ''}`}>
          {contractAccepted ? <ShieldCheck size={22} /> : <Bell size={22} />}
          <div><strong>{contractAccepted ? 'Compte à jour' : 'Une action est requise'}</strong><span>{contractAccepted ? 'Le contrat est accepté.' : 'Le contrat doit être vérifié.'}</span></div>
        </div>
      </section>

      <section className="parent-v0-kpis">
        <button onClick={() => setActivePage('homework')}><BookOpenCheck /><div><strong>{filteredHomework.length}</strong><span>Travaux</span></div></button>
        <button onClick={() => setActivePage('announcements')}><Megaphone /><div><strong>{filteredAnnouncements.length}</strong><span>Annonces</span></div></button>
        <button onClick={() => setActivePage('payments')}><CircleDollarSign /><div><strong>{familyRemaining} DT</strong><span>Reste famille</span></div></button>
        <button onClick={() => setActivePage('complaints')}><MessageCircleQuestion /><div><strong>{selectedComplaints.filter((item) => !['resolved', 'closed'].includes(complaintStatus(item.status))).length}</strong><span>Réclamations ouvertes</span></div></button>
      </section>

      <section className="parent-dashboard-grid-v0">
        <article className="parent-card-v0 span-two">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">À VENIR</p><h3>Travail à la maison</h3></div><button onClick={() => setActivePage('homework')}>Tout voir</button></div>
          {upcomingHomework.length ? upcomingHomework.map((item) => <div className="parent-list-row" key={item.id}><div className="parent-list-icon"><BookOpenCheck /></div><div><strong>{item.title || item.subject || 'Travail à faire'}</strong><span>{item.subject || item.teacherName || 'Classe'} · échéance {formatDate(item.deadline || item.dueDate)}</span></div></div>) : <div className="parent-empty">Aucun travail publié pour le moment.</div>}
        </article>
        <article className="parent-card-v0">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">ACTUALITÉS</p><h3>Dernières annonces</h3></div></div>
          {filteredAnnouncements.slice(0, 3).map((item) => <div className="announcement-item" key={item.id}><strong>{item.title || item.subject || 'Annonce'}</strong><span>{item.message || item.content || ''}</span></div>)}
          {!filteredAnnouncements.length && <div className="parent-empty">Aucune annonce.</div>}
        </article>
        <article className="parent-card-v0">
  <div className="parent-card-v0-heading">
    <div>
      <p className="eyebrow">JOURNÉE</p>
      <h3>Emploi du temps</h3>
    </div>

    <button
      type="button"
      onClick={() => setActivePage('timetable')}
    >
      Tout voir
    </button>
  </div>

  {classTimetableLoading && (
    <div className="parent-empty">
      Chargement de l'emploi du temps…
    </div>
  )}

  {!classTimetableLoading && classTimetableUrl && (
    <div className="parent-dashboard-timetable-preview">
      <img
        src={classTimetableUrl}
        alt={`Emploi du temps de ${
          selectedChild
            ? childName(selectedChild)
            : "l'enfant"
        }`}
      />
    </div>
  )}

  {!classTimetableLoading &&
    !classTimetableUrl &&
    selectedTimetable.slice(0, 4).map((item) => (
      <div className="timetable-mini" key={item.id}>
        <strong>{item.startTime || item.time || '—'}</strong>
        <span>{item.subject || item.title || 'Séance'}</span>
      </div>
    ))}

  {!classTimetableLoading &&
    !classTimetableUrl &&
    !selectedTimetable.length && (
      <div className="parent-empty">
        L’emploi du temps sera bientôt disponible.
      </div>
    )}
</article>
      </section>
    </>;
  };

  const renderPage = () => {
    if (activePage === 'dashboard') return renderDashboard();
    if (activePage === 'children') return <section><SectionTitle eyebrow="FAMILLE" title="Mes enfants" description="Choisissez un enfant pour personnaliser tout le portail."/><div className="parent-children-grid-v0">{children.map((child) => <button className={`parent-child-card-v0 ${selectedChild?.id === child.id ? 'selected' : ''}`} key={child.id} onClick={() => selectChild(child.id)}><div className="child-avatar large">{childName(child).charAt(0).toUpperCase()}</div><div><strong>{childName(child)}</strong><span>{child.className || child.level || child.requestedLevel || 'Classe à confirmer'}</span><small>{child.schoolYear || schoolYear}</small></div>{selectedChild?.id === child.id && <CheckCircle2 />}</button>)}</div></section>;
    if (activePage === 'attendance') {
      const absences = attendanceRecords.filter((item) => normalize(item.status) === 'absent');
      const lateArrivals = attendanceRecords.filter((item) => normalize(item.status) === 'late');

      return (
        <section>
          <SectionTitle
            eyebrow="SUIVI SCOLAIRE"
            title="Absences et retards"
            description={selectedChild ? `Historique de ${childName(selectedChild)}.` : 'Historique de l’enfant sélectionné.'}
          />

          <section className="parent-v0-kpis">
            <div><ClipboardCheck /><div><strong>{absences.length}</strong><span>Absence{absences.length > 1 ? 's' : ''}</span></div></div>
            <div><Clock3 /><div><strong>{lateArrivals.length}</strong><span>Retard{lateArrivals.length > 1 ? 's' : ''}</span></div></div>
          </section>

          {loadingAttendance && <div className="parent-empty large">Chargement des absences et retards…</div>}
          {!loadingAttendance && attendanceError && <div className="alert alert-error">{attendanceError}</div>}
          {!loadingAttendance && !attendanceError && attendanceRecords.length === 0 && (
            <div className="parent-empty large">Aucun retard ni aucune absence enregistré pour cet enfant.</div>
          )}
          {!loadingAttendance && !attendanceError && attendanceRecords.length > 0 && (
            <div className="parent-stack-v0">
              {attendanceRecords.map((item) => {
                const isLate = normalize(item.status) === 'late';
                return (
                  <article className="parent-card-v0" key={item.id}>
                    <div className="parent-card-v0-heading">
                      <div><p className="eyebrow">{isLate ? 'RETARD' : 'ABSENCE'}</p><h3>{item.subjectName || item.subject || 'Matière'}</h3></div>
                      <span className="parent-chip">{isLate ? `${item.lateMinutes || 0} min` : 'Absent'}</span>
                    </div>
                    <p><strong>Date :</strong> {formatDate(item.date)}</p>
                    {item.teacherName && <p className="muted">Enseignant : {item.teacherName}</p>}
                    {item.note && <p><strong>Remarque :</strong> {item.note}</p>}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      );
    }

    if (activePage === 'supplies') return <ParentSchoolSupplies selectedChild={selectedChild} user={user} parentId={profile?.parentId} defaultSchoolYear={schoolYear} />;
    if (activePage === 'homework') return <section><SectionTitle eyebrow="PÉDAGOGIE" title="Travail à la maison" description={`Travaux de ${selectedChild ? childName(selectedChild) : 'l’enfant sélectionné'}, classés par échéance.`}/><div className="parent-stack-v0">{filteredHomework.map((item) => <article className="parent-card-v0 homework-card-v0" key={item.id}><div><span className="parent-chip">{item.subject || 'Matière'}</span><h3>{item.title || 'Travail à faire'}</h3><p>{item.instructions || item.description || item.content || 'Consigne à consulter.'}</p></div><div className="homework-deadline"><CalendarDays/><span>À rendre le</span><strong>{formatDate(item.deadline || item.dueDate)}</strong></div></article>)}{!filteredHomework.length && <div className="parent-empty large">Aucun travail à la maison pour le moment.</div>}</div></section>;
    if (activePage === 'announcements') return <section><SectionTitle eyebrow="COMMUNICATION" title="Annonces" description="Annonces de l’école, du niveau et de la classe de l’enfant sélectionné."/><div className="parent-stack-v0">{filteredAnnouncements.map((item) => <article className="parent-card-v0" key={item.id}><span className="parent-chip">{item.audienceLabel || item.audienceType || 'École'}</span><h3>{item.title || item.subject || 'Annonce'}</h3><p>{item.message || item.content || ''}</p><small>{formatDate(item.publishedAt || item.createdAt)}</small></article>)}{!filteredAnnouncements.length && <div className="parent-empty large">Aucune annonce pour le moment.</div>}</div></section>;
    if (activePage === 'payments') {
      return (
        <section>
          <SectionTitle
            eyebrow="FINANCE"
            title="Contrats et paiements"
            description="Consultez la situation financière de la famille, l’historique des paiements et les contrats de vos enfants."
          />

          <ParentFinanceSection
            parentId={profile.parentId}
            parent={parent}
            children={children}
            user={user}
          />
        </section>
      );
    }

    if (activePage === 'timetable') {
  return (
    <section>
      <SectionTitle
        eyebrow="ORGANISATION"
        title="Emploi du temps"
        description={`Planning de ${
          selectedChild
            ? childName(selectedChild)
            : "l'enfant sélectionné"
        }.`}
      />

      {classTimetableLoading && (
        <div className="parent-empty large">
          Chargement de l'emploi du temps…
        </div>
      )}

      {!classTimetableLoading && classTimetableUrl && (
        <article className="parent-card-v0 parent-timetable-image-card">
          <img
            src={classTimetableUrl}
            alt={`Emploi du temps de ${
              selectedChild
                ? childName(selectedChild)
                : "l'enfant"
            }`}
          />
        </article>
      )}

      {!classTimetableLoading &&
        !classTimetableUrl &&
        selectedTimetable.length > 0 && (
          <div className="parent-stack-v0">
            {selectedTimetable.map((item) => (
              <article
                className="parent-card-v0 timetable-row-v0"
                key={item.id}
              >
                <strong>{item.day || item.weekday || 'Jour'}</strong>

                <span>
                  {item.startTime || '—'} – {item.endTime || '—'}
                </span>

                <b>
                  {item.subject || item.title || 'Séance'}
                </b>

                <small>
                  {item.teacherName || item.room || ''}
                </small>
              </article>
            ))}
          </div>
        )}

     

      {!classTimetableLoading &&
        !classTimetableUrl &&
        !selectedTimetable.length && (
          <div className="parent-empty large">
            L'emploi du temps sera ajouté prochainement.
          </div>
        )}
    </section>
  );
}

    if (activePage === 'notifications') {
      return (
        <section>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '16px',
              marginBottom: '22px'
            }}
          >
            <SectionTitle
              eyebrow="CENTRE DE NOTIFICATIONS"
              title="Notifications"
              description="Retrouvez ici les informations importantes envoyées par l’école."
            />

            {unreadCount > 0 && (
              <button
                type="button"
                className="button button-secondary"
                onClick={markAllNotificationsAsRead}
              >
                <CheckCircle2 size={16} />
                Tout marquer comme lu
              </button>
            )}
          </div>

          <div
            className="parent-card-v0"
            style={{
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <div>
              <strong>
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </strong>
              <p className="muted" style={{ margin: '4px 0 0' }}>
                {notifications.length} notification{notifications.length > 1 ? 's' : ''} au total
              </p>
            </div>

            <Bell size={22} />
          </div>

          <div className="parent-stack-v0">
            {notifications.map((notification) => {
              const isUnread = notification.read !== true;
              const title = notification.title || 'Notification';
              const message = notification.message || notification.body || '';

              return (
                <article
                  key={notification.id}
                  className="parent-card-v0"
                  style={{
                    borderColor: isUnread ? '#bfdbfe' : undefined,
                    background: isUnread ? '#f8fbff' : undefined
                  }}
                >
                  <div className="parent-card-v0-heading">
                    <div>
                      <p className="eyebrow">
                        {isUnread ? 'NOUVEAU' : 'NOTIFICATION'}
                      </p>
                      <h3>{title}</h3>
                    </div>

                    {isUnread ? (
                      <span className="parent-chip">Non lue</span>
                    ) : (
                      <span className="parent-chip">Lue</span>
                    )}
                  </div>

                  {message && <p>{message}</p>}

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      marginTop: '14px'
                    }}
                  >
                    <small>
                      {formatDateTime(
                        notification.createdAt ||
                        notification.updatedAt
                      )}
                    </small>

                    {isUnread && (
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={() => markNotificationAsRead(notification)}
                      >
                        <CheckCircle2 size={16} />
                        Marquer comme lue
                      </button>
                    )}
                  </div>
                </article>
              );
            })}

            {!notifications.length && (
              <div className="parent-empty large">
                Aucune notification pour le moment.
              </div>
            )}
          </div>
        </section>
      );
    }

if (activePage === 'complaints') {
      const status = selectedComplaint ? complaintStatus(selectedComplaint.status) : '';
      const history = Array.isArray(selectedComplaint?.statusHistory)
        ? [...selectedComplaint.statusHistory].sort((a, b) => toMillis(a.at) - toMillis(b.at))
        : [];
      const complaintStats = {
        open: selectedComplaints.filter((item) => !['resolved', 'closed'].includes(complaintStatus(item.status))).length,
        waiting: selectedComplaints.filter((item) => complaintStatus(item.status) === 'waiting_parent').length,
        resolved: selectedComplaints.filter((item) => complaintStatus(item.status) === 'resolved').length
      };
      const progressStage = ['resolved', 'closed'].includes(status)
        ? 3
        : ['in_review', 'waiting_parent'].includes(status)
          ? 2
          : status
            ? 1
            : 0;

      if (complaintComposerOpen) {
        return <section className="complaints-page-v3">
          <button type="button" className="complaint-back-v3" onClick={() => setComplaintComposerOpen(false)}>
            <span aria-hidden="true">←</span> Retour aux réclamations
          </button>

          <div className="complaint-single-heading-v3">
            <div>
              <p className="eyebrow">NOUVELLE RÉCLAMATION</p>
              <h2>Écrire à l’école</h2>
              <p>Expliquez simplement votre demande. Vous pourrez ensuite suivre son traitement depuis votre historique.</p>
            </div>
          </div>

          <div className="complaint-compose-card-v3">
            <form className="complaint-compose-form-v2" onSubmit={submitComplaint}>
              <fieldset>
                <legend>Quel est le sujet de votre demande ?</legend>
                <div className="complaint-category-grid-v2">
                  {[
                    ['administration', 'Administration', 'Dossier, inscription, document…'],
                    ['pedagogie', 'Pédagogie', 'Classe, apprentissage, suivi…'],
                    ['paiement', 'Paiement', 'Échéance, reçu, contrat…'],
                    ['autre', 'Autre', 'Pour toute autre demande']
                  ].map(([value, label, hint]) => <button
                    type="button"
                    key={value}
                    className={complaintForm.category === value ? 'selected' : ''}
                    onClick={() => setComplaintForm((current) => ({ ...current, category: value }))}
                  >
                    <strong>{label}</strong><span>{hint}</span>
                  </button>)}
                </div>
              </fieldset>

              <label>Objet
                <input
                  value={complaintForm.subject}
                  onChange={(e) => setComplaintForm((current) => ({ ...current, subject: e.target.value }))}
                  placeholder="Ex. Question concernant le dernier paiement"
                  required
                />
              </label>

              <label>Votre message
                <textarea
                  value={complaintForm.message}
                  onChange={(e) => setComplaintForm((current) => ({ ...current, message: e.target.value }))}
                  placeholder="Décrivez votre demande avec les informations utiles…"
                  required
                />
              </label>

              <div className="complaint-compose-actions-v2">
                <button type="button" className="button button-secondary" onClick={() => setComplaintComposerOpen(false)}>Annuler</button>
                <button className="button button-primary" disabled={sendingComplaint || !complaintForm.subject.trim() || !complaintForm.message.trim()}>
                  <Send size={16}/>{sendingComplaint ? 'Envoi…' : 'Envoyer la réclamation'}
                </button>
              </div>
            </form>
          </div>
        </section>;
      }

      if (selectedComplaint) {
        return <section className="complaints-page-v3 complaint-detail-page-v3">
          <button type="button" className="complaint-back-v3" onClick={() => setSelectedComplaintId('')}>
            <span aria-hidden="true">←</span> Retour aux réclamations
          </button>

          <article className="parent-complaint-detail-v3">
            <header className="parent-complaint-detail-head-v3">
              <div>
                <div className="complaint-detail-kicker-v2">
                  <span className="complaint-category-label-v2">{complaintCategoryLabel(selectedComplaint.category)}</span>
                  <span>Envoyée le {formatDate(selectedComplaint.createdAt)}</span>
                </div>
                <h2>{selectedComplaint.subject || 'Réclamation'}</h2>
                {selectedComplaint.studentName && <p>Concernant {selectedComplaint.studentName}</p>}
              </div>
              <span className={`complaint-status-v3 status-${status}`}>{complaintStatusLabel(status)}</span>
            </header>

            <section className="complaint-status-panel-v3">
              <div className="complaint-status-panel-title-v3">
                <div>
                  <span>État de la demande</span>
                  <strong>{complaintStatusLabel(status)}</strong>
                </div>
                <small>Dernière mise à jour : {formatDate(selectedComplaint.updatedAt || selectedComplaint.createdAt)}</small>
              </div>
              <div className="complaint-progress-v3" aria-label={`Statut : ${complaintStatusLabel(status)}`}>
                <div className={progressStage >= 1 ? 'active' : ''}><i/><span>Envoyée</span></div>
                <div className={progressStage >= 2 ? 'active' : ''}><i/><span>En cours</span></div>
                <div className={progressStage >= 3 ? 'active' : ''}><i/><span>Traitée</span></div>
              </div>
            </section>

            {status === 'waiting_parent' && <div className="complaint-callout-v2 attention">
              <Reply size={20}/>
              <div><strong>L’administration attend votre réponse</strong><span>Consultez le dernier message puis répondez ci-dessous.</span></div>
            </div>}
            {status === 'resolved' && <div className="complaint-callout-v2 success">
              <CheckCircle2 size={20}/>
              <div><strong>Cette réclamation a été traitée</strong><span>Les échanges restent disponibles dans votre historique.</span></div>
            </div>}

            <section className="complaint-thread-v3">
              <div className="complaint-thread-title-v3">
                <div><strong>Discussion</strong><span>Vos échanges avec l’administration</span></div>
                <small>{complaintMessages.length + 1} message{complaintMessages.length ? 's' : ''}</small>
              </div>

              <div className="complaint-message-v2 from-parent">
                <div className="complaint-message-meta-v2"><strong>Vous</strong><time>{formatDateTime(selectedComplaint.createdAt)}</time></div>
                <p>{selectedComplaint.message || 'Aucun message.'}</p>
              </div>

              {complaintMessages.map((message) => <div key={message.id} className={`complaint-message-v2 ${message.authorType === 'parent' ? 'from-parent' : 'from-admin'}`}>
                <div className="complaint-message-meta-v2"><strong>{message.authorType === 'parent' ? 'Vous' : (message.authorName || 'Administration')}</strong><time>{formatDateTime(message.createdAt)}</time></div>
                <p>{message.text}</p>
              </div>)}

              {!complaintMessages.length && selectedComplaint.adminResponse && <div className="complaint-message-v2 from-admin">
                <div className="complaint-message-meta-v2"><strong>Administration</strong><time>{formatDateTime(selectedComplaint.respondedAt)}</time></div>
                <p>{selectedComplaint.adminResponse}</p>
              </div>}
            </section>

            {!['resolved', 'closed'].includes(status) && <form className={`complaint-reply-composer-v3 ${status === 'waiting_parent' ? 'highlight' : ''}`} onSubmit={submitComplaintReply}>
              <label>Répondre à l’administration</label>
              <textarea
                value={complaintReply}
                onChange={(e) => setComplaintReply(e.target.value)}
                placeholder={status === 'waiting_parent' ? 'Écrivez votre réponse…' : 'Ajouter une précision ou un complément…'}
                required
              />
              <div>
                <span>{status === 'waiting_parent' ? 'Votre réponse relancera automatiquement le traitement.' : 'Vous pouvez compléter votre demande tant qu’elle est ouverte.'}</span>
                <button className="button button-primary" disabled={sendingComplaintReply || !complaintReply.trim()}>
                  <Send size={16}/>{sendingComplaintReply ? 'Envoi…' : 'Envoyer'}
                </button>
              </div>
            </form>}

            <details className="complaint-history-v3">
              <summary>Historique du traitement</summary>
              <div className="complaint-history-list-v2">
                <div><i/><p><strong>Réclamation envoyée</strong><span>{formatDateTime(selectedComplaint.createdAt)}</span></p></div>
                {history.filter((event) => complaintStatus(event.status) !== 'sent').map((event, index) => <div key={`${toMillis(event.at)}-${index}`}>
                  <i/><p><strong>{complaintStatusLabel(event.status)}</strong><span>{formatDateTime(event.at)}{event.byType === 'administration' && event.byName ? ` · ${event.byName}` : ''}</span></p>
                </div>)}
              </div>
            </details>
          </article>
        </section>;
      }

      return <section className="complaints-page-v3">
        <div className="complaints-page-heading-v3">
          <SectionTitle
            eyebrow="RELATION ÉCOLE-PARENT"
            title="Mes réclamations"
            description="Envoyez une nouvelle demande ou consultez simplement le suivi de vos demandes précédentes."
          />
          <button className="button button-primary complaints-new-button-v3" onClick={() => setComplaintComposerOpen(true)}>
            <MessageCircleQuestion size={17}/> Nouvelle réclamation
          </button>
        </div>

        {complaintStats.waiting > 0 && <div className="complaint-summary-alert-v3">
          <Reply size={19}/>
          <div><strong>{complaintStats.waiting} réclamation{complaintStats.waiting > 1 ? 's' : ''} attend{complaintStats.waiting > 1 ? 'ent' : ''} votre réponse</strong><span>Ouvrez la demande concernée pour répondre à l’administration.</span></div>
        </div>}

        <div className="complaint-history-heading-v3">
          <div>
            <h3>Historique de mes demandes</h3>
            <span>{selectedComplaints.length} réclamation{selectedComplaints.length > 1 ? 's' : ''}</span>
          </div>
          <div className="complaint-filter-tabs-v3" role="tablist" aria-label="Filtrer les réclamations">
            <button className={complaintFilter === 'all' ? 'active' : ''} onClick={() => setComplaintFilter('all')}>Toutes</button>
            <button className={complaintFilter === 'open' ? 'active' : ''} onClick={() => setComplaintFilter('open')}>En cours</button>
            <button className={complaintFilter === 'resolved' ? 'active' : ''} onClick={() => setComplaintFilter('resolved')}>Traitées</button>
          </div>
        </div>

        <div className="complaint-history-cards-v3">
          {visibleComplaints.map((item) => {
            const itemStatus = complaintStatus(item.status);
            const needsReply = itemStatus === 'waiting_parent';
            return <button
              key={item.id}
              type="button"
              className={`complaint-history-card-v3 ${needsReply ? 'needs-reply' : ''}`}
              onClick={() => setSelectedComplaintId(item.id)}
            >
              <div className="complaint-history-card-main-v3">
                <div className="complaint-history-card-top-v3">
                  <span className="complaint-category-label-v2">{complaintCategoryLabel(item.category)}</span>
                  <span>{formatDate(item.createdAt)}</span>
                </div>
                <h4>{item.subject || 'Réclamation'}</h4>
                <p>Dernière mise à jour : {formatDate(item.updatedAt || item.createdAt)}</p>
              </div>
              <div className="complaint-history-card-side-v3">
                <span className={`complaint-status-v3 status-${itemStatus}`}>{complaintStatusLabel(itemStatus)}</span>
                {needsReply && <small>Votre réponse est attendue</small>}
                <b>Voir le suivi <span aria-hidden="true">→</span></b>
              </div>
            </button>;
          })}

          {!visibleComplaints.length && <div className="complaint-empty-v3">
            <MessageCircleMore size={30}/>
            <strong>{selectedComplaints.length ? 'Aucune demande dans cette catégorie' : 'Aucune réclamation pour le moment'}</strong>
            <span>{selectedComplaints.length ? 'Essayez un autre filtre.' : 'Lorsque vous écrirez à l’école, vos demandes apparaîtront ici avec leur statut.'}</span>
            {!selectedComplaints.length && <button className="button button-primary" onClick={() => setComplaintComposerOpen(true)}>Nouvelle réclamation</button>}
          </div>}
        </div>
      </section>;
    }

    if (editingProfile) return <ParentProfileForm
      parent={parent}
      children={children}
      user={user}
      onCancel={() => setEditingProfile(false)}
      onSaved={(updatedProfile) => {
        const primary = updatedProfile.guardian1 || {};
        setParent((current) => ({
          ...current,
          fullName: primary.fullName || current?.fullName,
          fullname: primary.fullName || current?.fullname,
          phone: primary.phone || '',
          email: primary.email || '',
          cin: primary.cin || '',
          profession: primary.profession || '',
          relationship: primary.relationship || '',
          address: primary.address || '',
          onboardingProfile: updatedProfile
        }));
        setEditingProfile(false);
        setNotice('Vos informations ont été mises à jour.');
      }}
    />;

    const declared = parent?.onboardingProfile || {};
    const guardian1 = declared.guardian1 || {};
    const guardian2 = declared.guardian2 || {};
    const emergency = declared.emergencyContact || {};
    const familyStatus = ({ married: 'Mariés', divorced: 'Divorcés', separated: 'Séparés', widowed: 'Veuf / veuve', other: 'Autre' })[declared.familyStatus] || 'Non renseignée';
    const relationship = (value) => ({ mother: 'Mère', father: 'Père', guardian: 'Tuteur légal', other: 'Autre' })[value] || value || 'Responsable légal';
    const declaredChildren = Array.isArray(declared.children) ? declared.children : [];
    const contactLabel = (guardian, fallback) => {
      const relationLabel = guardian?.relationship ? relationship(guardian.relationship) : fallback;
      return guardian?.fullName ? `${relationLabel} — ${guardian.fullName}` : relationLabel;
    };
    const preferredContactDisplay = declared.preferredContact === 'guardian1'
      ? contactLabel(guardian1, 'Responsable légal 1')
      : declared.preferredContact === 'guardian2'
        ? contactLabel(guardian2, 'Responsable légal 2')
        : 'Les deux responsables';

    return <section>
      <div className="parent-profile-heading-v1">
        <SectionTitle eyebrow="MON COMPTE" title="Informations parent" description="Consultez et mettez à jour les informations communiquées à l’école."/>
        <button className="button button-primary" onClick={() => setEditingProfile(true)}><Pencil size={16}/> Modifier mes informations</button>
      </div>

      <div className="parent-profile-grid-v1">
        <article className="parent-card-v0 parent-profile-card-v1">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">FAMILLE</p><h3>Situation familiale</h3></div></div>
          <dl className="parent-details">
            <div><dt>Situation</dt><dd>{familyStatus}</dd></div>
            <div><dt>Contact prioritaire</dt><dd>{preferredContactDisplay}</dd></div>
            <div><dt>Décision judiciaire signalée</dt><dd>{declared.courtDecision ? 'Oui' : 'Non'}</dd></div>
          </dl>
        </article>

        <article className="parent-card-v0 parent-profile-card-v1">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">RESPONSABLE 1</p><h3>{guardian1.fullName || displayName}</h3></div></div>
          <dl className="parent-details">
            <div><dt>Lien</dt><dd>{relationship(guardian1.relationship)}</dd></div>
            <div><dt>CIN</dt><dd>{guardian1.cin || 'Non renseignée'}</dd></div>
            <div><dt>Profession</dt><dd>{guardian1.profession || 'Non renseignée'}</dd></div>
            <div><dt>Téléphone</dt><dd><Phone size={15}/>{guardian1.phone || parent?.phone || 'Non renseigné'}</dd></div>
            <div><dt>Email</dt><dd>{guardian1.email || parent?.email || profile?.email || user?.email || 'Non renseigné'}</dd></div>
            <div><dt>Adresse</dt><dd>{guardian1.address || parent?.address || 'Non renseignée'}</dd></div>
          </dl>
        </article>

        <article className="parent-card-v0 parent-profile-card-v1">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">RESPONSABLE 2</p><h3>{guardian2.fullName || 'Deuxième responsable'}</h3></div></div>
          {guardian2.fullName ? <dl className="parent-details">
            <div><dt>Lien</dt><dd>{relationship(guardian2.relationship)}</dd></div>
            <div><dt>CIN</dt><dd>{guardian2.cin || 'Non renseignée'}</dd></div>
            <div><dt>Profession</dt><dd>{guardian2.profession || 'Non renseignée'}</dd></div>
            <div><dt>Téléphone</dt><dd>{guardian2.phone || 'Non renseigné'}</dd></div>
            <div><dt>Email</dt><dd>{guardian2.email || 'Non renseigné'}</dd></div>
          </dl> : <div className="parent-empty">Aucun deuxième responsable renseigné.</div>}
        </article>

        <article className="parent-card-v0 parent-profile-card-v1 span-two">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">ENFANTS</p><h3>Informations des enfants</h3></div></div>
          <div className="parent-profile-children-v1">
            {children.map((child) => {
              const details = declaredChildren.find((item) => item.studentId === child.id) || {};
              return <div key={child.id}>
                <div className="child-avatar small">{childName(child).charAt(0).toUpperCase()}</div>
                <div><strong>{childName(child)}</strong><span>{child.className || child.level || child.requestedLevel || 'Classe à confirmer'}</span><small>{details.birthDate ? `Né(e) le ${details.birthDate}${details.birthPlace ? ` à ${details.birthPlace}` : ''}` : 'Date de naissance non renseignée'}</small></div>
              </div>;
            })}
          </div>
        </article>

        <article className="parent-card-v0 parent-profile-card-v1">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">URGENCE</p><h3>Contact et récupération</h3></div></div>
          <dl className="parent-details">
            <div><dt>Contact d’urgence</dt><dd>{emergency.fullName || 'Non renseigné'}</dd></div>
            <div><dt>Téléphone</dt><dd>{emergency.phone || 'Non renseigné'}</dd></div>
            <div><dt>Récupération</dt><dd>{declared.legalGuardiansOnlyPickup !== false ? 'Responsables légaux uniquement' : `${declared.authorizedPickup?.length || 0} personne(s) autorisée(s)`}</dd></div>
          </dl>
        </article>

        <article className="parent-card-v0 parent-profile-card-v1">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">NOTIFICATIONS</p><h3>Notifications</h3></div></div>
          <p>Activez les notifications pour recevoir les annonces et rappels importants sur cet appareil.</p>
          <button className="button button-primary" onClick={enableNotifications} disabled={notificationBusy || notificationRegistered}><Bell size={16}/>{notificationBusy ? 'Activation…' : notificationRegistered ? 'Notifications activées' : 'Activer les notifications'}</button>
          <small className="notification-help-v0">Sur iPhone, installez d’abord le site sur l’écran d’accueil depuis Safari.</small>
        </article>
      </div>
    </section>;
  };

  return <>
    {notificationPromptOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-prompt-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'grid',
          placeItems: 'center',
          padding: '20px',
          background: 'rgba(15, 23, 42, 0.68)',
          backdropFilter: 'blur(6px)'
        }}
      >
        <section
          style={{
            width: 'min(100%, 430px)',
            overflow: 'hidden',
            borderRadius: '26px',
            background: '#ffffff',
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.32)',
            border: '1px solid rgba(255,255,255,.7)'
          }}
        >
          <div
            style={{
              position: 'relative',
              padding: '28px 26px 24px',
              color: '#ffffff',
              background:
                'linear-gradient(135deg, #173f97 0%, #245bd8 100%)'
            }}
          >
            <button
              type="button"
              aria-label="Fermer"
              onClick={() => snoozeNotificationPrompt(3)}
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                width: '36px',
                height: '36px',
                border: 0,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                color: '#ffffff',
                background: 'rgba(255,255,255,.14)'
              }}
            >
              <X size={19} />
            </button>

            <div
              style={{
                width: '56px',
                height: '56px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '18px',
                marginBottom: '18px',
                background: 'rgba(255,255,255,.16)',
                border: '1px solid rgba(255,255,255,.22)'
              }}
            >
              <Bell size={27} />
            </div>

            <p
              style={{
                margin: '0 0 7px',
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '1.5px',
                opacity: 0.82
              }}
            >
              LE DÉFI • ESPACE PARENT
            </p>

            <h2
              id="notification-prompt-title"
              style={{
                margin: 0,
                fontSize: '26px',
                lineHeight: 1.12
              }}
            >
              {needsIOSInstall
                ? 'Recevez les notifications sur votre iPhone'
                : notificationState === 'denied'
                  ? 'Notifications désactivées'
                  : 'Ne manquez aucune information'}
            </h2>

            <p
              style={{
                margin: '10px 0 0',
                lineHeight: 1.55,
                opacity: 0.92
              }}
            >
              {needsIOSInstall
                ? 'Installez d’abord Le Défi sur votre écran d’accueil, puis activez les notifications.'
                : notificationState === 'denied'
                  ? 'Les notifications ont été bloquées pour Le Défi sur cet appareil.'
                  : 'Recevez les informations importantes de l’école directement sur votre téléphone.'}
            </p>
          </div>

          <div style={{ padding: '22px 24px 24px' }}>
            {needsIOSInstall ? (
              <>
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#334155',
                    lineHeight: 1.55
                  }}
                >
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      color: '#0f172a'
                    }}
                  >
                    Sur iPhone :
                  </strong>
                  <span>
                    Ouvrez Le Défi dans Safari, touchez
                    {' '}<strong>Partager</strong>, puis
                    {' '}<strong>Sur l’écran d’accueil</strong>.
                    Ouvrez ensuite Le Défi depuis la nouvelle
                    icône : l’activation des notifications vous
                    sera proposée.
                  </span>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginTop: '18px'
                  }}
                >
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => snoozeNotificationPrompt(3)}
                  >
                    Plus tard
                  </button>

                  <button
                    type="button"
                    className="button button-primary"
                    onClick={() =>
                      setNotificationPromptOpen(false)
                    }
                  >
                    J’ai compris
                  </button>
                </div>
              </>
            ) : notificationState === 'denied' ? (
              <>
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '16px',
                    background: '#fff7ed',
                    border: '1px solid #fed7aa',
                    color: '#9a3412',
                    lineHeight: 1.55
                  }}
                >
                  Autorisez les notifications pour Le Défi dans
                  les réglages de votre navigateur ou de votre
                  téléphone, puis revenez sur cette page.
                </div>

                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => snoozeNotificationPrompt(7)}
                  style={{
                    width: '100%',
                    marginTop: '18px'
                  }}
                >
                  Fermer
                </button>
              </>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gap: '10px',
                    marginBottom: '18px'
                  }}
                >
                  {[
                    [Megaphone, 'Annonces importantes de l’école'],
                    [CircleDollarSign, 'Paiements et informations financières'],
                    [ClipboardCheck, 'Absences, retards et suivi scolaire']
                  ].map(([Icon, label]) => (
                    <div
                      key={label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '11px 12px',
                        borderRadius: '14px',
                        background: '#f8fafc'
                      }}
                    >
                      <span
                        style={{
                          width: '36px',
                          height: '36px',
                          flex: '0 0 36px',
                          display: 'grid',
                          placeItems: 'center',
                          borderRadius: '11px',
                          background: '#eaf1ff',
                          color: '#245bd8'
                        }}
                      >
                        <Icon size={18} />
                      </span>
                      <strong
                        style={{
                          fontSize: '14px',
                          color: '#1e293b'
                        }}
                      >
                        {label}
                      </strong>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="button button-primary"
                  onClick={enableNotifications}
                  disabled={notificationBusy}
                  style={{
                    width: '100%',
                    minHeight: '48px'
                  }}
                >
                  <Bell size={17} />
                  {notificationBusy
                    ? 'Activation…'
                    : notificationState === 'granted'
                      ? 'Finaliser l’activation'
                      : 'Activer les notifications'}
                </button>

                <button
                  type="button"
                  onClick={() => snoozeNotificationPrompt(3)}
                  style={{
                    width: '100%',
                    marginTop: '8px',
                    padding: '10px',
                    border: 0,
                    background: 'transparent',
                    color: '#64748b',
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                >
                  Plus tard
                </button>

                <p
                  style={{
                    margin: '8px 0 0',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '12px',
                    lineHeight: 1.4
                  }}
                >
                  Vous pourrez modifier ce choix plus tard depuis
                  votre profil.
                </p>
              </>
            )}
          </div>
        </section>
      </div>
    )}

    <main className="parent-portal-v0">
    {mobileMenuOpen && <button className="parent-menu-scrim" aria-label="Fermer le menu" onClick={() => setMobileMenuOpen(false)}/>} 
    <aside className={`parent-sidebar-v0 ${mobileMenuOpen ? 'open' : ''}`}>
      <div className="parent-brand-v0"><img src="/logo.png" alt="Le Défi"/><div><strong>Le Défi</strong><span>Espace parent</span></div><button className="parent-close-menu" onClick={() => setMobileMenuOpen(false)}><X/></button></div>
      <nav>{MENU.map(({id,label,icon:Icon}) => <button key={id} className={activePage === id ? 'active' : ''} onClick={() => {setActivePage(id);setMobileMenuOpen(false);}}><Icon/><span>{label}</span>{id === 'notifications' && unreadCount > 0 && <b style={{ marginLeft: 'auto', minWidth: '22px', height: '22px', padding: '0 6px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#dc2626', color: '#fff', fontSize: '11px' }}>{unreadCount > 99 ? '99+' : unreadCount}</b>}</button>)}</nav>
      <button className="parent-signout-v0" onClick={logout}><LogOut/><span>Déconnexion</span></button>
    </aside>

    <section className="parent-workspace-v0">
      <header className="parent-header-v0">
        <button className="parent-mobile-menu" onClick={() => setMobileMenuOpen(true)}><Menu/></button>
        <div className="parent-selected-child-v0"><div className="child-avatar small">{selectedChild ? childName(selectedChild).charAt(0).toUpperCase() : '—'}</div><label><span>Enfant sélectionné</span><select value={selectedChild?.id || ''} onChange={(e) => selectChild(e.target.value)} disabled={!children.length}>{children.map((child) => <option value={child.id} key={child.id}>{childName(child)} — {child.className || child.level || child.requestedLevel || 'Classe à confirmer'}</option>)}</select></label><ChevronDown/></div>
        <button
          type="button"
          className={`parent-notification-button ${unreadCount > 0 ? 'has-unread' : ''}`}
          onClick={() => setActivePage('notifications')}
          title="Notifications"
        >
          <Bell/>
          {unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                minWidth: '19px',
                height: '19px',
                padding: '0 5px',
                borderRadius: '999px',
                background: '#dc2626',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #fff'
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </header>
      <div className="parent-page-v0">
        {error && <div className="alert alert-error parent-alert">{error}</div>}
        {notice && <div className="alert parent-notice-v0"><Sparkles size={16}/>{notice}<button onClick={() => setNotice('')}>×</button></div>}
        {!selectedChild && <div className="parent-empty large">Aucun enfant n’est lié à ce compte. Contactez l’administration.</div>}
        {selectedChild && renderPage()}
      </div>
    </section>
  </main>{devVersionBadge}</>;
}